import React, { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Task, Phase } from '../types';

interface TaskCardProps {
  task: Task;
  index: number;
  phase: Phase;
  onDeleteTask?: (taskId: string) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, index, phase, onDeleteTask, onUpdateTask }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(task.name);
  const [editDescription, setEditDescription] = useState(task.description);
  const isDone = phase.name.toLowerCase() === 'done';

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${task.name}"?`)) {
      onDeleteTask?.(task.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditName(task.name);
    setEditDescription(task.description);
  };

  const handleSave = () => {
    if (editName.trim()) {
      onUpdateTask?.(task.id, {
        name: editName.trim(),
        description: editDescription.trim()
      });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName(task.name);
    setEditDescription(task.description);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="task-card editing">
        <div className="task-edit-form">
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Task name"
            autoFocus
            className="task-edit-input"
          />
          <textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Task description (optional)"
            className="task-edit-textarea"
          />
          <div className="task-edit-buttons">
            <button onClick={handleSave} className="task-save-btn">Save</button>
            <button onClick={handleCancel} className="task-cancel-btn">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`task-card ${snapshot.isDragging ? 'dragging' : ''} ${isDone ? 'done' : ''}`}
        >
          <div className="task-card-header">
            <div {...provided.dragHandleProps} className="drag-handle">⋮⋮</div>
            <div className="task-card-actions">
              {onUpdateTask && (
                <button
                  className="edit-task-btn"
                  onClick={handleEdit}
                  title="Edit task"
                >
                  ✏️
                </button>
              )}
              {onDeleteTask && (
                <button
                  className="delete-task-btn"
                  onClick={handleDelete}
                  title="Delete task"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className={`task-title ${isDone ? 'done' : ''}`}>{task.name}</div>
          {task.description && (
            <div className="task-description">{task.description}</div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default TaskCard;